/* eslint-disable camelcase */
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { Box, Divider, Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { CheckCircleOutline, ErrorOutline, RadioButtonUnchecked } from '@mui/icons-material';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllSendingOrderProduct, getSendArticles } from 'src/shared/services/sendingService';
import { SendingOrderProduct } from 'src/modules/sending/models/sendingOrderProduct';
import { SendArticle } from 'src/modules/sending/models/sendArticle';
import { useSendingWizard } from 'src/modules/sending/contexts/sendingWizardContext';
import { computeSegments, CHANNEL_COLOR } from 'src/modules/sending/functions/deliveryReach';
import { computePricing } from 'src/modules/sending/functions/sendingPricing';

// Formaterar belopp som svenska kronor med två decimaler.
const formatSek = (value: number): string => `${value.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;

const METHOD_LABELS: Record<string, string> = { mail: 'E-post', kivra: 'Kivra', letter: 'Brev', pdf: 'PDF-nedladdning' };
const METHOD_COLOR = CHANNEL_COLOR;

// En sektion i översiktskortet: rubriken ligger ensam i vänsterkolumnen, alla värdetexter i högerkolumnen.
// Avdelad med linje (utom sista). Staplas ovanpå varandra på små skärmar.
const section = (label: string, content: ReactNode, last = false): ReactNode => (
  <Box
    sx={{
      px: 3,
      py: 2,
      borderBottom: last ? 'none' : '1px solid',
      borderColor: 'divider',
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      gap: { xs: 0.75, sm: 3 },
    }}
  >
    <Box sx={{ flex: { sm: '0 0 34%' }, minWidth: { sm: 120 } }}>
      <Typography variant='subtitle2' sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.primary' }}>
        {label}
      </Typography>
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>{content}</Box>
  </Box>
);

// Sista steget: sammanfattning av hela beställningen + prisberäkning. Kunden ser utskickstyp, urval,
// leveransmetoder med antal per kanal, väljer färg på brevet (påverkar pris live) och ser en kostnadstabell.
export const SendingWizardStep7Summary = () => {
  const isMounted = useIsMounted();
  const { watch, setValue } = useFormContext();
  const {
    coverage,
    selectionTab,
    dueDateFrom,
    dueDateTom,
    profileFilters,
    excludeProfileFilters,
    emailTextConfirmed,
    invoiceTextConfirmed,
    led,
    reminderPreviousPayDate,
  } = useSendingWizard();

  const [products, setProducts] = useState<SendingOrderProduct[]>([]);
  const [articles, setArticles] = useState<SendArticle[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, articlesRes] = await Promise.all([getAllSendingOrderProduct(), getSendArticles()]);
        if (!isMounted()) return;
        setProducts(productsRes.data);
        setArticles(articlesRes.data);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error hämta produkter/artiklar', e);
      }
    };
    fetchData();
  }, [isMounted]);

  const selectedProductId = watch('product_id') as number | undefined;
  const productName = products.find((p) => p.id === selectedProductId)?.name ?? '';
  // Utskickstyp härleds ur produktnamnet (typ-fält saknas): innehåller "påminnel" => påminnelseavisering.
  const isReminder = productName.toLowerCase().includes('påminnel');
  const typeLabel = isReminder ? 'Påminnelseavisering' : 'Medlemsavisering';

  const completeLater = !!watch('completeLater');
  const selectionDescription = (watch('selectionDescription') as string) ?? '';

  // Förfallodatum-text i sammanfattningen: intervall när båda datum finns, annars "t.o.m." / "från" (inte ett streck
  // med tomt start-/slutdatum).
  const dueRangeText = (() => {
    const from = dueDateFrom ? dueDateFrom.format('YYYY-MM-DD') : '';
    const tom = dueDateTom ? dueDateTom.format('YYYY-MM-DD') : '';
    if (from && tom) return `Förfallodatum ${from} – ${tom}`;
    if (tom) return `Förfallodatum t.o.m. ${tom}`;
    if (from) return `Förfallodatum från ${from}`;
    return '';
  })();

  const orderString = (watch('deliveryMethodOrder') as string) ?? '';
  const order = useMemo(() => (orderString ? orderString.split(',').filter((k) => k.length > 0) : []), [orderString]);

  // Färgval styr brev-artikeln (och därmed priset). Standard = svartvitt.
  const letterColor = ((watch('letterColor') as string) || 'bw') === 'color' ? 'color' : 'bw';
  const isColor = letterColor === 'color';

  const pre = coverage?.approx ? '≈ ' : '';

  // Antal per kanal – antal NYA som varje leveransmetod når (samma logik som leveranssteget; PDF når alla återstående).
  const segments = useMemo(() => computeSegments(order, coverage), [order, coverage]);
  const channelCounts = useMemo(() => segments.map((s) => ({ key: s.key, count: s.inc })), [segments]);
  // Brev-färgval visas bara när det faktiskt finns brev att skicka (letter-segmentet > 0), inte enbart för att brev valts som fallback.
  const hasLetter = segments.some((s) => s.key === 'letter' && s.inc > 0);

  const pricing = useMemo(() => computePricing(channelCounts, articles, isReminder, isColor), [channelCounts, articles, isReminder, isColor]);

  const textsStatus = useMemo(() => {
    // "Kompletteras senare" är ett giltigt val (neutralt). Ej klara texter utan det valet blockerar inskick → varna rött.
    if (completeLater) return { done: false, warn: false, text: 'Texterna kompletteras senare' };
    const needsEmail = order.includes('mail') || order.includes('kivra');
    const done = (!needsEmail || emailTextConfirmed) && invoiceTextConfirmed;
    return { done, warn: !done, text: done ? 'Texterna är ifyllda och klarmarkerade' : 'Texterna är inte klarmarkerade ännu' };
  }, [completeLater, order, emailTextConfirmed, invoiceTextConfirmed]);

  const statusRow = (done: boolean, text: string, warn = false) => {
    let icon = <RadioButtonUnchecked fontSize='small' color='disabled' />;
    if (done) icon = <CheckCircleOutline fontSize='small' color='success' />;
    else if (warn) icon = <ErrorOutline fontSize='small' color='error' />;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant='body2' color={warn ? 'error.main' : 'text.secondary'} sx={{ fontWeight: warn ? 600 : 400 }}>
          {text}
        </Typography>
      </Box>
    );
  };

  return (
    <GridWrapper>
      {/* Vänster: översikt av beställningen – tydliga sektioner i ett kort, avdelade med linjer. */}
      <Grid item xs={12} md={7}>
        <Box sx={{ mb: 2 }}>
          <Typography variant='h5' sx={{ fontWeight: 700, mb: 0.5 }}>
            Sammanfattning
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Kontrollera beställningen och den beräknade kostnaden innan du skickar in.
          </Typography>
        </Box>
        <Box sx={{ overflow: 'hidden' }}>
          {section(
            'Typ av utskick',
            <>
              <Typography variant='body1' sx={{ fontWeight: 600 }}>
                {typeLabel}
              </Typography>
              {productName && (
                <Typography variant='body2' color='text.secondary'>
                  {productName}
                </Typography>
              )}
            </>,
          )}

          {section(
            'Urval',
            <>
              {/* Fritext-läget visar kundens egen beskrivning. Urvals-lägena visar en kort rubrik + detaljerna nedan i grått. */}
              {selectionTab === 1 ? (
                <Typography variant='body1' sx={{ fontWeight: 600 }} color={selectionDescription ? 'text.primary' : 'text.disabled'}>
                  {selectionDescription || 'Ingen urvalsbeskrivning angiven'}
                </Typography>
              ) : (
                <Typography variant='body1' sx={{ fontWeight: 600 }}>
                  Medlemmar med
                </Typography>
              )}
              {dueRangeText && (
                <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                  {dueRangeText}
                </Typography>
              )}
              {profileFilters.length > 0 && (
                <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                  <b>Inkludera:</b> {profileFilters.map((f) => f.itemName).join(', ')}
                </Typography>
              )}
              {excludeProfileFilters.length > 0 && (
                <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                  <b>Exkludera:</b> {excludeProfileFilters.map((f) => f.itemName).join(', ')}
                </Typography>
              )}
              {coverage && (
                <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                  {pre}
                  {coverage.total} {coverage.total === 1 ? 'faktura' : 'fakturor'}
                  {coverage.membersCovered !== coverage.total ? ` (${coverage.membersCovered} medlemmar)` : ''}
                </Typography>
              )}
            </>,
          )}

          {section(
            'Leveransmetoder',
            order.length === 0 ? (
              <Typography variant='body1' color='text.disabled'>
                Ingen leveransmetod vald
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {segments.map((s, i) => (
                  <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: METHOD_COLOR[s.key] ?? 'grey.400', flexShrink: 0 }} />
                    <Typography variant='body1' sx={{ flex: 1, fontWeight: 600 }}>
                      {i + 1}. {METHOD_LABELS[s.key] ?? s.key}
                    </Typography>
                    <Typography variant='body1' sx={{ fontWeight: 700 }}>
                      {pre}
                      {s.inc} st
                    </Typography>
                  </Box>
                ))}
              </Box>
            ),
          )}

          {hasLetter &&
            section(
              'Färg på brevet',
              <>
                <ToggleButtonGroup
                  exclusive
                  size='small'
                  value={letterColor}
                  onChange={(_e, v) => {
                    if (v) setValue('letterColor', v, { shouldValidate: true });
                  }}
                >
                  <ToggleButton value='bw' sx={{ textTransform: 'none', px: 2.5 }}>
                    Svartvitt
                  </ToggleButton>
                  <ToggleButton value='color' sx={{ textTransform: 'none', px: 2.5 }}>
                    Färg
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant='body2' color='text.secondary' sx={{ mt: 0.75 }}>
                  Färgvalet påverkar brevets pris.
                </Typography>
              </>,
            )}

          {section('Texter', statusRow(textsStatus.done, textsStatus.text), true)}

          {section(
            'Meddelande till Föreningssupport',
            <>
              <ControlledTextField name='message' label='Skriv ett meddelande (valfritt)' fullWidth multiline minRows={3} />
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.75 }}>
                T.ex. särskilda önskemål eller frågor om beställningen.
              </Typography>
            </>,
          )}
        </Box>
      </Grid>

      {/* Höger: kostnadssammanställning */}
      <Grid item xs={12} md={5}>
        <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', p: 6, height: '100%' }}>
          <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
            Beräknad kostnad
          </Typography>

          {(() => {
            // En påminnelse kan inte prissättas i förväg: kostnaden beror på hur många fakturor som fortfarande är
            // obetalda när påminnelsen produceras, och huvudutskickets urval säger inget om det.
            if (led === 0) return null;

            return (
              <Typography variant='body1' color='text.secondary' sx={{ mt: 1 }}>
                Kostnaden kan inte beräknas i förväg – den beror på hur många fakturor som är obetalda när påminnelsen produceras
                {reminderPreviousPayDate ? `, tidigast efter förfallodagen ${reminderPreviousPayDate.format('D MMM YYYY')}` : ''}.
              </Typography>
            );
          })()}

          {led === 0 && !coverage && (
            <Typography variant='body1' color='text.secondary' sx={{ mt: 1 }}>
              Gör ett urval i Medlemmar-steget för att se den beräknade kostnaden.
            </Typography>
          )}

          {led === 0 && coverage && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5, mb: 2 }}>
                <Typography variant='h4' sx={{ fontWeight: 700, lineHeight: 1 }}>
                  {formatSek(pricing.gross)}
                  <Box component='sup' sx={{ fontSize: '0.5em', fontWeight: 600, ml: 0.25 }}>
                    *
                  </Box>
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  inkl. moms
                </Typography>
              </Box>

              {pricing.lines.map((line) => (
                <Box key={line.key} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.75 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant='body1' sx={{ fontWeight: 600 }}>
                      {line.label}
                    </Typography>
                    {!line.hideDetail && (
                      <Typography variant='body2' color='text.secondary'>
                        {line.free && `${pre}${line.count} st – ingen kostnad`}
                        {!line.free && line.priceMissing && `${pre}${line.count} st – pris kunde inte hämtas`}
                        {!line.free && !line.priceMissing && `${pre}${line.count} st × ${formatSek(line.unitPrice ?? 0)}`}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant='body1' sx={{ fontWeight: 700 }}>
                    {line.free && '0 kr'}
                    {!line.free && line.priceMissing && '—'}
                    {!line.free && !line.priceMissing && formatSek(line.total)}
                  </Typography>
                </Box>
              ))}

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant='body2' color='text.secondary'>
                  Netto (exkl. moms)
                </Typography>
                <Typography variant='body2'>{formatSek(pricing.net)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant='body2' color='text.secondary'>
                  Moms 25 %
                </Typography>
                <Typography variant='body2'>{formatSek(pricing.vat)}</Typography>
              </Box>

              {coverage.approx && (
                <Typography variant='body2' color='text.secondary' sx={{ display: 'block', mt: 1.5 }}>
                  Antalen är ungefärliga, så kostnaden kan avvika något.
                </Typography>
              )}

              {/* Disclaimer: priset bygger på antalet just nu och kan ändras om urvalet ändras fram till utskicket. */}
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 2 }}>
                * Kostnaden beräknas utifrån det aktuella antalet just nu. Om antalet mottagare i urvalet förändras fram tills utskicket görs kommer
                kostnaden bli högre eller lägre.
              </Typography>
            </>
          )}
        </Box>
      </Grid>
    </GridWrapper>
  );
};
